import gql from "graphql-tag";
import {VeriflytResultFragment} from "../../Queries/Veriflyt";
import {IsnResultWithHeaderFragment} from "../../Queries/Isn";
import {useMutation} from "@apollo/react-hooks";
import {useContext, useState} from "react";
import {OrgDataContext} from "../../context/orgData";
import {
    VeriflytResultFragmentFragment,
    IsnResultWithHeaderFragmentFragment,
    MutationRunPaxNodeComplianceEngineCheckArgs,
    PaxNode, ComplianceEngineResultFragmentFragment, PaxNodeComplianceFieldsFragmentFragment
} from "../../__generated__/graphql/types";
import {PaxNodeComplianceFieldsFragment} from "../../Queries/Compliance";

const COMPLIANCE_MUTATION = gql`
    mutation BulkComplianceChecker_Mutation($paxNodeID: ID!){
        RunPaxNodeComplianceEngineCheck(paxNodeID: $paxNodeID){
            ...PaxNodeComplianceFieldsFragment
        }
    }
    ${PaxNodeComplianceFieldsFragment}
`

type PaxNodeStatuses = {
    [paxNodeID: string]: Status
}

type Status = {
    loading: boolean,
    error: Error,
    result: ComplianceEngineResultFragmentFragment
}

export interface Options {
    onCompleted?: (statuses: PaxNodeStatuses) => void
}

export default function useBulkComplianceChecker(options?: Options){

    type ResultType = {
        RunPaxNodeComplianceEngineCheck: PaxNodeComplianceFieldsFragmentFragment
    }

    const [ invokeMutation ] = useMutation<
        ResultType,
        MutationRunPaxNodeComplianceEngineCheckArgs
    >(COMPLIANCE_MUTATION);

    const [ statuses, setStatuses ] = useState<PaxNodeStatuses>({});

    const orgData = useContext(OrgDataContext);

    function clearStatuses(){
        setStatuses({});
    }

    function setStatus(paxNodeID: string, status: Status){
        setStatuses((prev) => ({
            ...prev,
            [paxNodeID]: status
        }));
    }

    function getStatus(paxNodeID: string): Status{
        const status = statuses[paxNodeID];
        if (!status){
            return {
                loading: false,
                error: null,
                result: null
            }
        }
        return status;
    }

    async function startCheck(paxNodeID: string){
        try {
            setStatus(paxNodeID, { loading: true, error: null, result: null })
            const result = await invokeMutation({
                variables: {
                    paxNodeID: paxNodeID
                }
            })

            const pax = result.data.RunPaxNodeComplianceEngineCheck;
            console.debug(`Compliance check for ${pax.lastName}, ${pax.firstName} (${paxNodeID}) is complete!`);
            setStatus(paxNodeID, { loading: false, error: null, result: pax?.lastComplianceEngineResult });
        }
        catch (err){
            console.warn(`ISN Check for pax ID ${paxNodeID} has failed! Check error log below.`);
            console.error(err);
            setStatus(paxNodeID, { loading: false, error: err, result: null });
        }
    }

    function beginChecks(paxNodeIDs: string[]){
        clearStatuses();

        const promises = paxNodeIDs.map((paxNodeID) => {
            return startCheck(paxNodeID);
        })

        return Promise.all(promises)
            .then(() => {
                setStatuses((statuses) => {
                    // We're not actually going to set the state here.
                    // Instead, we are retrieving the statuses at the latest state and sending the same state back.
                    options?.onCompleted?.(statuses);
                    return statuses;
                })
            })
    }

    return {
        begin: beginChecks,
        isRunning: isRunning(statuses),
        isLoading: (paxNodeID: string) => {
            return getStatus(paxNodeID)?.loading || false;
        },
        hasError: (paxNodeID: string) => {
            return getStatus(paxNodeID)?.error || null;
        },
        getResult: (paxNodeID: string) => {
            return getStatus(paxNodeID)?.result || null;
        }
    }
}

function isRunning(statuses: PaxNodeStatuses){
    for (let statusesKey in statuses) {
        const status = statuses[statusesKey];

        if (status.loading){
            return true;
        }
    }

    return false;
}